import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import {
  deleteImageGeneratorSpecification,
  getImageGeneratorAssetPreview,
  getImageGeneratorErrorMessage,
  isImageGeneratorSpecificationReference,
  uploadImageGeneratorSpecification,
} from "../../app/api/imageGenerator";
import {
  SPECIFICATION_IMAGE_ACCEPT,
  SPECIFICATION_IMAGE_DELETE_ERROR,
  SPECIFICATION_IMAGE_LABEL,
  SPECIFICATION_IMAGE_MAX_BYTES,
  SPECIFICATION_IMAGE_MAX_COUNT,
  SPECIFICATION_IMAGE_MIME_TYPES,
  SPECIFICATION_IMAGE_PREVIEW_ALT,
  SPECIFICATION_IMAGE_REMOVE_LABEL,
  SPECIFICATION_IMAGE_SIZE_ERROR,
  SPECIFICATION_IMAGE_TYPE_ERROR,
  SPECIFICATION_IMAGE_UPLOAD_ERROR,
  SPECIFICATION_IMAGE_UPLOAD_LABEL,
  SPECIFICATION_IMAGE_UPLOADING_LABEL,
} from "../../constants/imageGeneratorDictionaries";
import { chipClassName, FieldLabel } from "./ImageGeneratorControls";

type Props = {
  imageUrl: string;
  /** Bumped when the brief resets so an in-flight upload cannot restore the image. */
  resetKey: number;
  onImageUrlChange: (url: string) => void;
  onActiveChange: (active: boolean) => void;
  onUploadingChange: (uploading: boolean) => void;
};

const isAllowedSpecificationFile = (file: File): boolean => {
  const mime = (file.type || "").toLowerCase();
  if (mime === "image/jpg") {
    return true;
  }
  if ((SPECIFICATION_IMAGE_MIME_TYPES as readonly string[]).includes(mime)) {
    return true;
  }
  if (mime) {
    return false;
  }
  const name = file.name.toLowerCase();
  return (
    name.endsWith(".png") || name.endsWith(".jpg") || name.endsWith(".jpeg")
  );
};

const ImageGeneratorSpecificationUpload: React.FC<Props> = ({
  imageUrl,
  resetKey,
  onImageUrlChange,
  onActiveChange,
  onUploadingChange,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const uploadSeq = useRef(0);
  const localPreviewRef = useRef("");
  const remotePreviewRef = useRef("");
  const keepLocalForUrl = useRef("");
  const imageUrlRef = useRef(imageUrl);
  const resetKeyRef = useRef(resetKey);
  const onActiveChangeRef = useRef(onActiveChange);
  const onUploadingChangeRef = useRef(onUploadingChange);
  const [localPreview, setLocalPreview] = useState("");
  const [remotePreview, setRemotePreview] = useState("");
  const [uploading, setUploading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  onActiveChangeRef.current = onActiveChange;
  onUploadingChangeRef.current = onUploadingChange;

  const setPreview = (next: string) => {
    const previous = localPreviewRef.current;
    if (previous && previous !== next && previous.startsWith("blob:")) {
      URL.revokeObjectURL(previous);
    }
    localPreviewRef.current = next;
    setLocalPreview(next);
  };

  const clearRemotePreview = useCallback(() => {
    const previous = remotePreviewRef.current;
    if (previous.startsWith("blob:")) {
      URL.revokeObjectURL(previous);
    }
    remotePreviewRef.current = "";
    setRemotePreview("");
  }, []);

  const releaseUnreferencedSpecification = (url: string) => {
    const value = url.trim();
    if (!isImageGeneratorSpecificationReference(value)) {
      return;
    }
    void deleteImageGeneratorSpecification(value).catch(error => {
      toast.error(
        getImageGeneratorErrorMessage(error, SPECIFICATION_IMAGE_DELETE_ERROR)
      );
    });
  };

  useEffect(() => {
    return () => {
      uploadSeq.current += 1;
      const previous = localPreviewRef.current;
      if (previous.startsWith("blob:")) {
        URL.revokeObjectURL(previous);
      }
      const remote = remotePreviewRef.current;
      if (remote.startsWith("blob:")) {
        URL.revokeObjectURL(remote);
      }
      onUploadingChangeRef.current(false);
    };
  }, []);

  const cancelLocalSpecification = () => {
    uploadSeq.current += 1;
    setPreview("");
    setUploading(false);
    onUploadingChangeRef.current(false);
  };

  useEffect(() => {
    const previousUrl = imageUrlRef.current;
    imageUrlRef.current = imageUrl;
    if (previousUrl === imageUrl) {
      return;
    }
    if (keepLocalForUrl.current === imageUrl) {
      keepLocalForUrl.current = "";
      return;
    }
    setPreview("");
  }, [imageUrl]);

  useEffect(() => {
    const value = imageUrl.trim();
    if (
      !value ||
      /^https?:\/\//i.test(value) ||
      value.startsWith("blob:") ||
      value.startsWith("data:")
    ) {
      clearRemotePreview();
      return;
    }
    let cancelled = false;
    void getImageGeneratorAssetPreview(value)
      .then(src => {
        if (cancelled) {
          if (src.startsWith("blob:")) {
            URL.revokeObjectURL(src);
          }
          return;
        }
        const previous = remotePreviewRef.current;
        if (previous && previous !== src && previous.startsWith("blob:")) {
          URL.revokeObjectURL(previous);
        }
        remotePreviewRef.current = src.startsWith("blob:") ? src : "";
        setRemotePreview(src);
      })
      .catch(() => {
        if (!cancelled) {
          clearRemotePreview();
        }
      });
    return () => {
      cancelled = true;
    };
  }, [clearRemotePreview, imageUrl]);

  useEffect(() => {
    if (resetKeyRef.current === resetKey) {
      return;
    }
    resetKeyRef.current = resetKey;
    cancelLocalSpecification();
  }, [resetKey]);

  const previewSrc =
    localPreview ||
    remotePreview ||
    (/^https?:\/\//i.test(imageUrl) ? imageUrl : "");
  const active = Boolean(previewSrc);

  useEffect(() => {
    onActiveChangeRef.current(active);
  }, [active]);

  useEffect(() => {
    if (!previewSrc) {
      setPreviewOpen(false);
    }
  }, [previewSrc]);

  useEffect(() => {
    if (!previewOpen) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPreviewOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [previewOpen]);

  const handleFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = Array.from(event.target.files || []).slice(
      0,
      SPECIFICATION_IMAGE_MAX_COUNT
    )[0];
    event.target.value = "";
    if (!file) {
      return;
    }
    if (!isAllowedSpecificationFile(file)) {
      toast.error(SPECIFICATION_IMAGE_TYPE_ERROR);
      return;
    }
    if (file.size > SPECIFICATION_IMAGE_MAX_BYTES) {
      toast.error(SPECIFICATION_IMAGE_SIZE_ERROR);
      return;
    }

    const seq = (uploadSeq.current += 1);
    const previousUrl = imageUrlRef.current;
    setPreview(URL.createObjectURL(file));
    setUploading(true);
    onUploadingChangeRef.current(true);
    try {
      const uploaded = await uploadImageGeneratorSpecification(file);
      const url = String(uploaded?.url || "").trim();
      if (!isImageGeneratorSpecificationReference(url)) {
        throw new Error(SPECIFICATION_IMAGE_UPLOAD_ERROR);
      }
      if (uploadSeq.current !== seq) {
        releaseUnreferencedSpecification(url);
        return;
      }
      keepLocalForUrl.current = url;
      onImageUrlChange(url);
      if (previousUrl && previousUrl !== url) {
        releaseUnreferencedSpecification(previousUrl);
      }
    } catch (error) {
      if (uploadSeq.current !== seq) {
        return;
      }
      setPreview("");
      toast.error(
        getImageGeneratorErrorMessage(error, SPECIFICATION_IMAGE_UPLOAD_ERROR)
      );
    } finally {
      if (uploadSeq.current === seq) {
        setUploading(false);
        onUploadingChangeRef.current(false);
      }
    }
  };

  const handleRemove = () => {
    const previousUrl = imageUrlRef.current;
    uploadSeq.current += 1;
    setPreview("");
    setUploading(false);
    onUploadingChangeRef.current(false);
    onImageUrlChange("");
    if (previousUrl) {
      releaseUnreferencedSpecification(previousUrl);
    }
  };

  return (
    <div className="min-w-0 w-full self-start">
      <FieldLabel>{SPECIFICATION_IMAGE_LABEL}</FieldLabel>
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={uploading}
        className={`${chipClassName(false)} disabled:cursor-wait disabled:opacity-60`}
      >
        {uploading
          ? SPECIFICATION_IMAGE_UPLOADING_LABEL
          : SPECIFICATION_IMAGE_UPLOAD_LABEL}
      </button>
      {previewSrc ? (
        <div className="relative mt-2 h-[72px] w-[72px] shrink-0">
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            aria-haspopup="dialog"
            aria-expanded={previewOpen}
            aria-label={SPECIFICATION_IMAGE_PREVIEW_ALT}
            className="flex h-full w-full cursor-zoom-in items-center justify-center overflow-hidden rounded-lg border border-[#E8DDD4] bg-white outline-none focus-visible:outline-none"
          >
            <img
              src={previewSrc}
              alt=""
              className="h-full w-full object-contain p-1"
            />
          </button>
          <button
            type="button"
            aria-label={SPECIFICATION_IMAGE_REMOVE_LABEL}
            title={SPECIFICATION_IMAGE_REMOVE_LABEL}
            onMouseDown={event => {
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={event => {
              event.preventDefault();
              event.stopPropagation();
              handleRemove();
            }}
            className="absolute right-0 top-0 z-10 flex h-4 w-4 translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border border-[#737373] bg-white text-[11px] leading-none text-secondary shadow-sm hover:bg-[#F5F5F5] focus:outline-none"
          >
            ×
          </button>
        </div>
      ) : null}
      <input
        ref={inputRef}
        type="file"
        accept={SPECIFICATION_IMAGE_ACCEPT}
        className="hidden"
        onChange={handleFile}
      />
      {previewOpen && previewSrc
        ? createPortal(
            <div
              className="fixed inset-0 z-[210] flex items-center justify-center bg-[#1F1F1F]/70 p-4 backdrop-blur-[2px]"
              role="dialog"
              aria-modal="true"
              aria-label={SPECIFICATION_IMAGE_PREVIEW_ALT}
              onClick={() => setPreviewOpen(false)}
            >
              <button
                type="button"
                onClick={() => setPreviewOpen(false)}
                className="absolute right-5 top-5 z-20 flex h-10 w-10 items-center justify-center rounded-full bg-white text-secondary shadow"
                aria-label="Close"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                  <path
                    d="M6 6l12 12M18 6 6 18"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
              <img
                src={previewSrc}
                alt={SPECIFICATION_IMAGE_PREVIEW_ALT}
                className="max-h-[90vh] max-w-[min(96vw,1400px)] rounded-2xl object-contain shadow-2xl"
                onClick={event => event.stopPropagation()}
              />
            </div>,
            document.body
          )
        : null}
    </div>
  );
};

export default ImageGeneratorSpecificationUpload;
